// src/components/gestionEvento/ExportInscritosModal.js

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  CircularProgress,
  Grid,
} from '@mui/material';

import { handleExportLogic } from '../../exportTypes/exportInscritosLogic';
import FieldsSelectorChips from '../../exportTypes/FieldsSelector';

const ExportInscritosModal = ({
  open,
  onClose,
  inscripciones,
  competitionName,
  competitionId,
}) => {
  const [exportType, setExportType] = useState('full');
  const [meetReportType, setMeetReportType] = useState('D');
  const [selectedFields, setSelectedFields] = useState([]);
  const [delimiter, setDelimiter] = useState(',');
  const [forceQuoted, setForceQuoted] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Lista de campos base + 2 items especiales: "Pruebas" y "Relevos"
  // (SIN añadir "Prueba 1", "Marca 1", etc.)
  const allFields = [
    'CompetitionID', 'CompetitionName', 'CodigoInscripcion',
    'Nombre', 'Apellido', 'Email', 'Teléfono', 'Rama', 'Categoría',
    'CatNumber', 'Pruebas', 'Relevos', 'Dirección', 'Ciudad', 'Estado', 'País',
    'FechaRegistro', 'PaymentMethod', 'Asociacion', 'DiaNacimiento',
    'MesNacimiento', 'AnioNacimiento', 'FotoPerfil', 'LogoEquipo',
    'EquipoNombre', 'EquipoTeamCode', 'ContactoNombre', 'ContactoTeléfono',
    'TutorNombre', 'TutorEmail', 'TutorTeléfono',
  ];

  useEffect(() => {
    if (!open) {
      setIsExporting(false);
    }
  }, [open]);

  const handleExport = () => {
    setIsExporting(true);
    setTimeout(() => {
      handleExportLogic({
        exportType,         // 'full', 'hytek', 'custom'
        meetReportType,     // 'D', 'I', 'Q'
        selectedFields,     // los campos elegidos
        inscripciones,
        competitionName,
        competitionId,
        delimiter,
        forceQuoted,
      });
      setIsExporting(false);
      onClose();
    }, 1200);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Exportar Inscritos</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Seleccione el tipo de exportación y configure las opciones.
        </DialogContentText>

        {/* 1) Tipo de Exportación */}
        <FormControl component="fieldset" sx={{ mt: 2 }}>
          <FormLabel component="legend">Tipo de Exportación</FormLabel>
          <RadioGroup
            row
            value={exportType}
            onChange={(e) => setExportType(e.target.value)}
          >
            <FormControlLabel value="full" control={<Radio />} label="Completa" />
            <FormControlLabel value="custom" control={<Radio />} label="Personalizada" />
            <FormControlLabel value="hytek" control={<Radio />} label="Hy‑Tek Meet Manager" />
          </RadioGroup>
        </FormControl>

        {/* 2) Opciones Hy-Tek */}
        {exportType === 'hytek' && (
          <Box sx={{ mt: 2 }}>
            <FormLabel component="legend">Reporte para Meet Manager</FormLabel>
            <RadioGroup
              row
              value={meetReportType}
              onChange={(e) => setMeetReportType(e.target.value)}
            >
              <FormControlLabel value="D" control={<Radio />} label="Reporte D" />
              <FormControlLabel value="I" control={<Radio />} label="Reporte I" />
              <FormControlLabel value="Q" control={<Radio />} label="Reporte Q" />
            </RadioGroup>
          </Box>
        )}

        {/* 3) Opciones "Personalizada" */}
        {exportType === 'custom' && (
          <Box sx={{ mt: 2 }}>
            <FormLabel component="legend">
              Seleccione los campos a exportar
            </FormLabel>
            <Box sx={{ mt: 1 }}>
              <FieldsSelectorChips
                allFields={allFields}
                selectedFields={selectedFields}
                setSelectedFields={setSelectedFields}
                allLabel="(Todos los campos)"
              />
            </Box>
          </Box>
        )}

        {/* 4) Opciones de delimitador / forzar texto (solo full/custom) */}
        {(exportType === 'full' || exportType === 'custom') && (
          <Box sx={{ mt: 2 }}>
            <FormLabel component="legend">Opciones de Formato</FormLabel>
            <Box sx={{ display: 'flex', gap: 3, mt: 3, flexWrap: 'wrap' }}>
              <Grid item xs={12} sm={12}>
                <FormLabel component="legend">Delimitador</FormLabel>
                <RadioGroup
                  row
                  value={delimiter}
                  onChange={(e) => setDelimiter(e.target.value)}
                >
                  <FormControlLabel value="," control={<Radio />} label=", (Coma)" />
                  <FormControlLabel value=";" control={<Radio />} label="; (Punto y Coma)" />
                  <FormControlLabel value="\t" control={<Radio />} label="(Tab)" />
                </RadioGroup>
              </Grid>
              <Grid item xs={12} sm={12}>
                <FormLabel component="legend">¿Forzar Texto para Excel?</FormLabel>
                <RadioGroup
                  row
                  value={forceQuoted ? 'yes' : 'no'}
                  onChange={(e) => setForceQuoted(e.target.value === 'yes')}
                >
                  <FormControlLabel value="yes" control={<Radio />} label="Sí (solo marcas)" />
                  <FormControlLabel value="no" control={<Radio />} label="No" />
                </RadioGroup>
              </Grid>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isExporting}>
          Cancelar
        </Button>
        <Button
          onClick={handleExport}
          variant="contained"
          color="primary"
          disabled={isExporting}
        >
          {isExporting ? (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={20} color="inherit" />
              <span>Exportando...</span>
            </Box>
          ) : (
            'Exportar'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ExportInscritosModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  inscripciones: PropTypes.array.isRequired,
  competitionName: PropTypes.string.isRequired,
  competitionId: PropTypes.string,
};

export default ExportInscritosModal;
